import type { Ref } from 'vue';
import sha256 from 'crypto-js/sha256';
import { useMainStore } from '../store';
import type { Lead, QuizData, QuestionAnswer } from '../types';
import {
  trackQuizSubmit,
  trackQuizGateSkip,
  getExposureTemplateId,
} from '../analytics/events';
import { hashEmail } from '../analytics/hash';
import { getSessionId } from '../analytics/session';
import { getVariantIdForSession, getAssignmentSource } from '../analytics/variant';
import type { AssignmentSource, VariantId } from '../analytics/variant';
import { buildRedirectUrl, skipUtmParams, navigateTop } from '../components/redirect';
import { resolveRedirectUrl } from '../store/redirect-resolution';

export interface LeadFormModel {
  name: string;
  email: string;
  phone?: string;
}

/**
 * The stamp values, by arm. Lowercase-with-hyphen is NOT cosmetic: the ad
 * wrapper's `sanitizeKvpValue` lowercases every GAM key-value, so a camelCase
 * `variantA` would arrive in GAM as `varianta` while GA4 kept `variantA` — the
 * two systems this stamp exists to JOIN would disagree on the label. These
 * values survive the sanitizer byte-identical, and match the convention the
 * live `skip-quiz` stamp already set (DEV-1418).
 */
const VARIANT_UTM_MEDIUM: Record<'A' | 'B', string> = {
  A: 'variant-a',
  B: 'variant-b',
};

/** An arm counts as declared only if it carries at least one override key. */
function isDeclaredArm(arm: unknown): boolean {
  return !!arm && typeof arm === 'object' && Object.keys(arm as object).length > 0;
}

/**
 * The submit-path A/B stamp — `{ utm_medium: 'variant-a' | 'variant-b' }` — or
 * an empty object when this session has no ACTIVE template-A/B assignment.
 *
 * Why utm_medium and why on the redirect: the landing page's GAM key-values are
 * built from the URL it was loaded with, and leadclump-flow was removed from
 * minhapme, so there is no session join between quiz and landing. Riding the
 * arm in on utm_medium is the only channel that survives the hop, which is what
 * makes per-variant revenue attribution possible for DEV-1135. DEV-1591.
 *
 * Callers must apply this AFTER copying the inbound UTMs, so it overrides them —
 * same ordering contract as skipUtmParams (DEV-1418).
 *
 * ASSIGNMENT-based, not exposure-based — deliberately. The stamp reflects which
 * arm the session was ASSIGNED to, exactly like `lead.variant_id` and the GA4
 * `variant_id` dimension, so all three systems label a session identically and
 * can be joined. It does NOT reflect whether `applyVariant` actually managed to
 * render the override: a silent applyVariant fallback (malformed arm, rejected
 * questions) leaves an assigned-B session rendering base UI but still stamped
 * `variant-b`. That is intention-to-treat, the correct posture — such a session
 * DILUTES the measured effect rather than desyncing the three systems against
 * each other. Read the `variants_error` event rate to detect dilution.
 *
 * Gates, all required, because `getVariantIdForSession` buckets EVERY session
 * off `pblk_sid` regardless of whether the quiz runs an experiment. Stamping on
 * the bucket alone would rewrite utm_medium on ~100% of fleet traffic the
 * moment this bundle ships:
 *
 *   1. NOT FORCED. `?pblk_variant=A|B` is an unauthenticated query param anyone
 *      can append, and `assignment_source` does NOT travel on the redirect URL —
 *      so a forced impression is indistinguishable from a real one once it
 *      reaches GAM, and there is no way to filter it back out of arm revenue.
 *      Forcing still drives what RENDERS (that's its job for QA and E2E); it
 *      just never contaminates the measurement stream.
 *   2. The quiz record must declare a `variants` block with at least one arm
 *      that is a NON-EMPTY object. `{ variants: { A: {} } }` — a partial DDB
 *      write or a future editor bug — would otherwise arm the rewrite while
 *      rendering base UI for everyone. No declared arm means no experiment, so
 *      the redirect URL must stay byte-identical to today.
 *   3. The arm must be A or B. In practice `getSessionId()` always returns a
 *      value on the web path, so the 'control' bucket is effectively unreachable
 *      in production; this guard is defense-in-depth against a future sid source
 *      that can fail, not a case we expect to hit.
 *
 * Both arms stamp, by design. With a one-armed block (`variants.B` only),
 * `variant-a` means "assigned to arm A = the base experience" — the revenue
 * comparison is meaningless without the baseline side, so A is labelled too
 * rather than left as unstamped inbound traffic.
 *
 * Floors: minhapme + rendaedinheiro were checked and target `utm_source` only,
 * so relabelling utm_medium cannot move a floor THERE. That is a snapshot of two
 * sites, not a fleet-wide guarantee — before rolling this to another destination
 * site, re-audit that site's pricing `url_parameters` for `utm_medium=` rows.
 * Checklist on the ClickUp card (DEV-1591).
 *
 * Note the arm is read from the ALREADY-BUILT lead, not from a second
 * `getVariantIdForSession` call, so the redirect stamp and `lead.variant_id`
 * can never diverge.
 */
export function variantUtmParams(
  quizData: Pick<QuizData, 'variants'> | null | undefined,
  variantId: VariantId | undefined,
  assignmentSource: AssignmentSource | undefined
): Record<string, string> {
  if (assignmentSource === 'forced') return {};
  if (variantId !== 'A' && variantId !== 'B') return {};

  const variants = quizData?.variants;
  if (!isDeclaredArm(variants?.A) && !isDeclaredArm(variants?.B)) return {};

  return { utm_medium: VARIANT_UTM_MEDIUM[variantId] };
}

/**
 * The gate money path — lead assembly, POST, cookies, analytics, redirect —
 * extracted from Gate.vue (DEV-1135) so the legacy Gate and the flow engine's
 * gate block share ONE implementation. Behavior is pinned by
 * useLeadSubmit.spec.ts; Gate.vue consumes this composable unchanged in
 * behavior.
 *
 * `consent` is the flow-engine addition (UTUA-style LGPD checkbox): when the
 * gate config declares consent_checkbox, the block passes the checkbox state
 * here and it rides on the lead payload as `consent`. Legacy gates pass
 * nothing and the field stays off the payload.
 */
export function useLeadSubmit(
  quizData: QuizData,
  form: Ref<LeadFormModel>,
  options?: { consent?: Ref<boolean> }
) {
  const store = useMainStore();

  const buildLead = (): Lead => {
    const lead: Lead = {} as Lead;
    lead.quiz_id = quizData.quiz_id || '';
    lead.site_id = quizData.site_id || '';

    const leadAnswers = store.getLeadAnswers as unknown as QuestionAnswer[];
    const direct_to = resolveRedirectUrl(
      store.quizUrlLogic,
      leadAnswers.map((answer) => answer.answer)
    );

    lead.direct_to = direct_to || quizData.quiz_default_url;
    lead.referrer = document.referrer;
    lead.origin = window.location.origin + window.location.pathname;

    lead.fbp = store._getCookie('_fbp');
    lead.gid = store._getCookie('_gid');

    const queryParams = new URLSearchParams(window.location.search);
    lead.utm_source = queryParams.get('utm_source') || undefined;
    lead.utm_campaign = queryParams.get('utm_campaign') || undefined;
    lead.utm_medium = queryParams.get('utm_medium') || undefined;
    lead.utm_content = queryParams.get('utm_content') || undefined;
    lead.utm_term = queryParams.get('utm_term') || undefined;

    lead.fbclid = queryParams.get('fbclid') || undefined;
    // gclid is sourced from the mount-time snapshot (store.sessionAttributes),
    // not from queryParams at submit — locks attribution to the original
    // landing URL. Issue #43.
    lead.gclid = store.sessionAttributes.gclid || undefined;
    lead.sub_id = queryParams.get('sub_id') || undefined;

    lead.session_attributes = store.sessionAttributes;

    // Experiment exposure at conversion time (DEV-1135 A/B): stamp the bucket
    // and the RENDERED template so ermes user data can attribute LTV to the
    // template that captured the lead. The legacy renderer stamps 'default'
    // (set by _resolveFlowConfig); the key is only absent when a lead is built
    // before the store resolved the flow, which the mount order prevents.
    lead.variant_id = getVariantIdForSession(getSessionId());
    lead.assignment_source = getAssignmentSource();
    const exposedTemplateId = getExposureTemplateId();
    if (exposedTemplateId) {
      lead.template_id = exposedTemplateId;
    }

    // Enrich answers with friendly texts (question title + answer label).
    lead.question_answers = leadAnswers.map((answer) => {
      const question = quizData.questions?.find((q) => q.id === answer.id);
      let answerText = answer.answer;
      if (question && question.answers) {
        const answerData = question.answers.find((ans) => {
          return Array.isArray(ans) && ans[0] === answer.answer;
        }) as string[] | undefined;
        if (answerData && Array.isArray(answerData)) {
          answerText = answerData[1] || answer.answer;
        }
      }
      return {
        id: question ? question.title : answer.id,
        answer: answerText,
      } as QuestionAnswer;
    });

    return lead;
  };

  // One-shot submit guard (DEV-1571). Two things called submit() more than
  // once per conversion: (1) every extra tap while the POST was in flight —
  // the CTA re-enables on `fetching=false` before the redirect lands — and
  // (2) the template gate SFCs declare no emits, so the block's `@submit`
  // listener ALSO fell through as a native `submit` listener on the template
  // root and one form submission reached this function twice, POSTing the
  // lead twice with a single click. Never released on success: the page is
  // navigating away, and a retry would only mint a duplicate lead row.
  let submitting = false;

  const submit = async (): Promise<void> => {
    if (submitting) return;
    submitting = true;
    try {
      await submitOnce();
    } catch (error) {
      // Only an unexpected throw releases the guard (createLead itself
      // swallows request errors), so a retry stays possible after a crash.
      submitting = false;
      throw error;
    }
  };

  const submitOnce = async (): Promise<void> => {
    const lead = buildLead();
    lead.name = form.value.name;
    lead.email = form.value.email;
    const normalizedEmail = (form.value.email || '').trim().toLowerCase();
    const normalizedName = (form.value.name || '').trim().toLowerCase();
    lead.external_id = sha256(normalizedEmail).toString();
    lead.hashed_email = sha256(normalizedEmail).toString();
    lead.hashed_name = sha256(normalizedName).toString();
    lead.phone = form.value.phone;
    if (options?.consent !== undefined) {
      lead.consent = options.consent.value === true;
    }

    // Read once, reuse for both API POST and redirect hash so leadclump-flow's
    // landing-side lead_check correlates to the same stored row.
    const session_id = getSessionId();
    lead.session_id = session_id;

    await store.createLead(lead);

    const additionalParams = {
      utm_source: lead.utm_source,
      utm_medium: lead.utm_medium,
      utm_campaign: lead.utm_campaign,
      utm_content: lead.utm_content,
      utm_term: lead.utm_term,
      fbclid: lead.fbclid,
      gclid: lead.gclid,
      sub_id: lead.sub_id,
      // Spread last so the variant stamp overrides the inbound utm_medium it
      // relabels. No-op on every quiz without a declared variants arm. Note it
      // relabels the REDIRECT only — `lead.utm_medium` above still carries the
      // true inbound value, which is what the lead row is attributed on.
      // DEV-1591.
      ...variantUtmParams(
        quizData,
        // Lead types these as plain strings for wire compatibility; they are
        // written by getVariantIdForSession/getAssignmentSource in buildLead.
        lead.variant_id as VariantId | undefined,
        lead.assignment_source as AssignmentSource | undefined
      ),
    } as Record<string, string>;

    // l_e / l_s are unconditional — leadclump-flow's landing verification is
    // the source of truth (see Gate history pre-extraction).
    const hashParams: Record<string, string> = {
      l_e: lead.hashed_email,
      l_s: session_id,
    };

    const redirect_url = buildRedirectUrl(lead.direct_to || '', additionalParams, hashParams);

    // Defense in depth: hashEmail('') would mint a stable "non-user" sha256
    // shared by every empty submission — never ship it to GAds.
    const hasEmail = !!(form.value.email && form.value.email.trim());
    const emailHash = hasEmail ? hashEmail(form.value.email) : '';

    trackQuizSubmit({
      quiz_id: quizData.quiz_id,
      email_sha256: emailHash,
      redirect_url,
    });

    if (hasEmail) {
      store._setCookie('leadclump_e_sha256', emailHash, 30);
    }

    // Persist gclid for Web GTM → conversions-api attribution (DEV-787).
    // 90-day TTL = default click-through attribution window; last-click
    // semantics are intentional (see pre-extraction Gate comment).
    if (lead.gclid) {
      store._setCookie('pblk_gclid', lead.gclid, 90);
    }

    store.resetQuiz();
    navigateTop(redirect_url);
  };

  const skip = (): void => {
    const lead = buildLead();

    const additionalParams = {
      utm_source: lead.utm_source,
      utm_medium: lead.utm_medium,
      utm_campaign: lead.utm_campaign,
      utm_content: lead.utm_content,
      utm_term: lead.utm_term,
      fbclid: lead.fbclid,
      gclid: lead.gclid,
      sub_id: lead.sub_id,
      // Spread last so the skip stamp overrides the inbound UTM it relabels.
      // DEV-1418.
      ...skipUtmParams(quizData),
    } as Record<string, string>;

    // Skip path has no email → no l_e; only the session correlation ID.
    const hashParams: Record<string, string> = { l_s: getSessionId() };

    const redirect_url = buildRedirectUrl(lead.direct_to || '', additionalParams, hashParams);

    trackQuizGateSkip({
      quiz_id: quizData.quiz_id,
      questions_answered: store.questionAnswers.length,
      redirect_url,
    });

    store.resetQuiz();
    navigateTop(redirect_url);
  };

  return { submit, skip, buildLead };
}
